// Configuración de API y endpoints
// ⚠️ IMPORTANTE: Este es el ÚNICO lugar donde se define la URL del backend
// Para cambiar la URL en desarrollo/producción, modificar el archivo .env
const rawApiUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:4007/api'
const rawWsUrl = import.meta.env.VITE_WS_URL || 'http://127.0.0.1:4007'
const API_BASE_URL = import.meta.env.DEV ? rawApiUrl.replace('localhost', '127.0.0.1') : rawApiUrl
const WS_BASE_URL = import.meta.env.DEV ? rawWsUrl.replace('localhost', '127.0.0.1') : rawWsUrl

// Exportar URLs base para uso en servicios
export { API_BASE_URL, WS_BASE_URL }

export const API_CONFIG = {
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
}

export const API_ENDPOINTS = {
  // Auth
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    register: '/auth/register',
    refresh: '/auth/refresh',
    forgotPassword: '/auth/forgot-password',
    resetPassword: '/auth/reset-password'
  },

  // Users (usando ruta correcta del backend)
  users: {
    list: '/usuarios',
    get: (id) => `/usuarios/${id}`,
    create: '/usuarios',
    update: (id) => `/usuarios/${id}`,
    delete: (id) => `/usuarios/${id}`,
    profile: '/usuarios/profile'
  },

  // Emergencies (usando ruta correcta del backend)
  emergencies: {
    list: '/emergencias',
    get: (id) => `/emergencias/${id}`,
    create: '/emergencias',
    update: (id) => `/emergencias/${id}`,
    assign: (id) => `/emergencias/${id}/assign`,
    complete: (id) => `/emergencias/${id}/complete`,
    cancel: (id) => `/emergencias/${id}/cancel`,
    track: (id) => `/emergencias/${id}/track`
  },

  // Medical Units (usando ruta correcta del backend)
  medicalUnits: {
    list: '/unidades_medicas',
    get: (id) => `/unidades_medicas/${id}`,
    create: '/unidades_medicas',
    update: (id) => `/unidades_medicas/${id}`,
    updateStatus: (id) => `/unidades_medicas/${id}/status`,
    updateLocation: (id) => `/unidades_medicas/${id}/location`,
    available: '/unidades_medicas/available'
  },

  // Affiliates (usando ruta correcta del backend)
  affiliates: {
    list: '/afiliados',
    get: (id) => `/afiliados/${id}`,
    create: '/afiliados',
    update: (id) => `/afiliados/${id}`,
    delete: (id) => `/afiliados/${id}`,
    activate: (id) => `/afiliados/${id}/activate`,
    suspend: (id) => `/afiliados/${id}/suspend`
  },

  // Contracts (usando ruta correcta del backend)
  contracts: {
    list: '/contratos',
    get: (id) => `/contratos/${id}`,
    create: '/contratos',
    update: (id) => `/contratos/${id}`,
    cancel: (id) => `/contratos/${id}/cancel`,
    renew: (id) => `/contratos/${id}/renew`
  },

  // Reports
  reports: {
    dashboard: '/reports/dashboard',
    emergencies: '/reports/emergencies',
    financial: '/reports/financial',
    performance: '/reports/performance',
    export: '/reports/export'
  },

  // Notifications
  notifications: {
    list: '/notifications',
    markAsRead: (id) => `/notifications/${id}/read`,
    markAllAsRead: '/notifications/read-all',
    preferences: '/notifications/preferences'
  },

  // Solicitudes de Contacto
  solicitudesContacto: {
    list: '/solicitudes_contacto',
    get: (id) => `/solicitudes_contacto/${id}`,
    create: '/solicitudes_contacto',
    update: (id) => `/solicitudes_contacto/${id}`,
    delete: (id) => `/solicitudes_contacto/${id}`,

    // Estados específicos
    pendientes: '/solicitudes_contacto/pendientes',
    respondidas: '/solicitudes_contacto/respondidas',
    cerradas: '/solicitudes_contacto/cerradas',
    estadisticas: '/solicitudes_contacto/estadisticas',

    // Filtros
    porTipo: (tipo) => `/solicitudes_contacto/tipo_consulta/${tipo}`,
    porPrioridad: (prioridad) => `/solicitudes_contacto/prioridad/${prioridad}`,
    porCorreo: (correo) => `/solicitudes_contacto/correo/${correo}`,

    // Acciones
    responder: (id) => `/solicitudes_contacto/${id}/responder`,
    cerrar: (id) => `/solicitudes_contacto/${id}/cerrar`,
    reabrir: (id) => `/solicitudes_contacto/${id}/reabrir`,
    asignar: (id) => `/solicitudes_contacto/${id}/asignar`,
    reasignar: (id) => `/solicitudes_contacto/${id}/reasignar`,
    cambiarPrioridad: (id) => `/solicitudes_contacto/${id}/prioridad`,
    cambiarEstado: (id) => `/solicitudes_contacto/${id}/estado`,
    activar: (id) => `/solicitudes_contacto/${id}/activar`,
    desactivar: (id) => `/solicitudes_contacto/${id}/desactivar`,

    // Operaciones masivas
    asignacionMasiva: '/solicitudes_contacto/masivo/asignar',
    cierreMasivo: '/solicitudes_contacto/masivo/cerrar',
    eliminacionMasiva: '/solicitudes_contacto/masivo/eliminar'
  },

  // Settings
  settings: {
    general: '/settings/general',
    update: '/settings/update',
    backup: '/settings/backup',
    restore: '/settings/restore'
  },

  // Surveys/Encuestas (integración con backend en español)
  surveys: {
    // Preguntas de encuestas
    questions: {
      list: '/preguntas_encuestas',
      get: (id) => `/preguntas_encuestas/${id}`,
      create: '/preguntas_encuestas',
      update: (id) => `/preguntas_encuestas/${id}`,
      delete: (id) => `/preguntas_encuestas/${id}`,
      activate: (id) => `/preguntas_encuestas/${id}/activar`,
      deactivate: (id) => `/preguntas_encuestas/${id}/desactivar`,
      reorder: '/preguntas_encuestas/reordenar',
      duplicate: (id) => `/preguntas_encuestas/duplicar/${id}`,
      changeState: (id) => `/preguntas_encuestas/${id}/cambiar_estado`
    },

    // Respuestas de encuestas
    responses: {
      list: '/respuestas_encuestas',
      get: (id) => `/respuestas_encuestas/${id}`,
      create: '/respuestas_encuestas',
      update: (id) => `/respuestas_encuestas/${id}`,
      delete: (id) => `/respuestas_encuestas/${id}`,
      activate: (id) => `/respuestas_encuestas/${id}/activar`,
      deactivate: (id) => `/respuestas_encuestas/${id}/desactivar`
    },

    // Estadísticas y reportes
    analytics: {
      general: '/respuestas_encuestas/estadisticas',
      nps: '/respuestas_encuestas/estadisticas/nps',
      satisfaction: '/respuestas_encuestas/reportes/satisfaccion',
      byCategory: '/respuestas_encuestas/reportes/por_categoria'
    },

    // Visualización de resultados
    results: {
      dashboard: '/respuestas_encuestas/resultados/dashboard',
      graphics: '/respuestas_encuestas/resultados/graficos',
      historic: '/respuestas_encuestas/resultados/historico',
      comparative: '/respuestas_encuestas/resultados/comparativo'
    },

    // Exportación
    export: {
      excel: '/respuestas_encuestas/exportar/excel',
      pdf: '/respuestas_encuestas/exportar/pdf'
    },

    // Configuración de encuestas
    configuration: {
      list: '/configuracion_encuestas',
      get: (id) => `/configuracion_encuestas/${id}`,
      create: '/configuracion_encuestas',
      update: (id) => `/configuracion_encuestas/${id}`,
      delete: (id) => `/configuracion_encuestas/${id}`,
      activate: (id) => `/configuracion_encuestas/${id}/activar`,
      deactivate: (id) => `/configuracion_encuestas/${id}/desactivar`,
      evaluationSystem: '/configuracion_encuestas/sistema_evaluacion',
      resetEvaluationSystem: '/configuracion_encuestas/sistema_evaluacion/reset',
      templates: '/configuracion_encuestas/plantillas',
      template: (id) => `/configuracion_encuestas/plantillas/${id}`
    },

    // Notificaciones masivas
    notifications: {
      sendMassive: '/encuestas/enviar_masivo',
      pendingDelivery: '/encuestas/pendientes_envio',
      reminder: (id) => `/encuestas/recordatorio/${id}`,
      deliveryStats: '/encuestas/estadisticas_envios'
    }
  }
}

// URLs externas
export const EXTERNAL_URLS = {
  googleMaps: 'https://maps.google.com',
  waze: 'https://waze.com',
  whatsapp: 'https://wa.me',
  support: 'https://helpmed.com/support',
  documentation: 'https://docs.helpmed.com'
}

// Configuración de WebSocket
export const WEBSOCKET_CONFIG = {
  url: WS_BASE_URL,
  reconnectInterval: 5000,
  maxReconnectAttempts: 10
}
